"use client";

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

export default function TimesheetsTab({ project }) {
  const [timesheets, setTimesheets] = useState([]);
  const [activeTab, setActiveTab] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const userTimezone = localStorage.getItem("userTimezone") || "UTC";
  const itemsPerPage = 50;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.timesheetSubTab) {
      setActiveTab(location.state.timesheetSubTab);
    }
  }, [location.state]);

  const fetchTimesheets = useCallback(
    async (page, status) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/project/${project._id}?page=${page}&limit=${itemsPerPage}&status=${status}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const { timesheets: data, total, pages } = response.data;
        setTimesheets(data || []);
        setTotalItems(total || 0);
        setTotalPages(pages || 1);
        setCurrentPage(page);
      } catch (err) {
        console.error("Error fetching timesheets:", err);
        setError(err.message || "Failed to fetch timesheets");
      } finally {
        setLoading(false);
      }
    },
    [project._id, itemsPerPage],
  );

  // Re-fetch when page, tab, or project changes
  useEffect(() => {
    fetchTimesheets(currentPage, activeTab);
  }, [currentPage, activeTab, fetchTimesheets]);

  // Reset to page 1 when switching tabs
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const tickIntervalId = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(tickIntervalId);
  }, []);

  const calculateTimeDifference = (
    start,
    end,
    timesheetTimezone,
    timesheetOffset,
    breaks = [],
  ) => {
    const startDate = new Date(start);
    let endDate = end ? new Date(end) : new Date();
    const differenceInMs = endDate - startDate;
    const totalBreakDurationMs = breaks.reduce(
      (sum, breakItem) =>
        sum + (parseInt(breakItem.duration, 10) || 0) * 60 * 1000,
      0,
    );
    return Math.max(
      0,
      Math.floor((differenceInMs - totalBreakDurationMs) / 1000),
    );
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (date) => {
    const localDate = new Date(date);
    return localDate.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: userTimezone,
    });
  };

  const formatTime = (date) => {
    const localDate = new Date(date);
    return localDate.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: userTimezone,
    });
  };

  const handleViewClick = (timesheet) => {
    const id = timesheet._id.$oid || timesheet._id;
    navigate(`/timesheet/${id}`, {
      state: { fromProject: true, projectId: project._id, activeTab },
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-400 text-center py-4">{error}</div>;
  }

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-md border border-gray-600">
      <h2 className="text-xl font-semibold text-white mb-4 tracking-tight">
        Timesheet Records
      </h2>
      <div className="border-b border-gray-600 mb-6">
        <nav className="flex space-x-4">
          {["Pending", "Approved", "Rejected"].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`py-2 px-4 text-sm font-medium rounded-t-md transition-colors duration-200 ${
                activeTab === tab
                  ? "bg-gray-600 text-orange-400 border-b-2 border-orange-400"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
              }`}
            >
              {tab}
              {activeTab === tab && totalItems > 0 && (
                <span className="ml-2 text-xs text-gray-300">
                  ({totalItems})
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div className="overflow-hidden rounded-lg shadow-xl">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                Project
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                WO#
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Desc
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Start
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                End
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Cost
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg actions-column">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {timesheets.length > 0 ? (
              timesheets.map((timesheet) => (
                <tr
                  key={timesheet._id.$oid || timesheet._id}
                  onClick={(e) => {
                    if (e.target.closest(".actions-column")) return;
                    handleViewClick(timesheet);
                  }}
                  className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {timesheet?.project?.projectName || "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {timesheet.woNumber || "N/A"}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-200 truncate max-w-[200px]">
                    {timesheet.title || "Pending"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {timesheet.displayName}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {formatDate(timesheet.start.$date || timesheet.start)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {formatTime(timesheet.start.$date || timesheet.start)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {timesheet.end
                      ? formatTime(timesheet.end.$date || timesheet.end)
                      : "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                    {timesheet.clockInStatus && !timesheet.end ? (
                      <div
                        key={`clock-${timesheet._id.$oid || timesheet._id}-${tick}`}
                        className="flex items-center space-x-2 bg-orange-400 rounded-full px-3 py-1 w-fit"
                      >
                        <svg
                          className="h-4 w-4 text-gray-200 animate-pulse"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-gray-200">
                          {formatDuration(
                            calculateTimeDifference(
                              timesheet.start.$date || timesheet.start,
                              null,
                              timesheet.timezone,
                              timesheet.timezoneOffset,
                              timesheet.breaks,
                            ),
                          )}
                        </span>
                      </div>
                    ) : timesheet.end ? (
                      <div className="flex items-center space-x-2 bg-gray-500 rounded-full px-3 py-1 w-fit">
                        <svg
                          className="h-4 w-4 text-gray-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-gray-200">
                          {formatDuration(
                            calculateTimeDifference(
                              timesheet.start.$date || timesheet.start,
                              timesheet.end.$date || timesheet.end,
                              timesheet.timezone,
                              timesheet.timezoneOffset,
                              timesheet.breaks,
                            ),
                          )}
                        </span>
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200 text-right">
                    {timesheet.totalCost
                      ? `$${Number(timesheet.totalCost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : "—"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-right actions-column">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewClick(timesheet);
                      }}
                      className="text-orange-400 hover:text-orange-300 font-medium transition-colors duration-200"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="10"
                  className="px-4 py-4 text-center text-sm text-gray-400"
                >
                  No {activeTab.toLowerCase()} timesheets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-400">
          Showing {timesheets.length} of {totalItems} · Page {currentPage} of{" "}
          {totalPages}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-500 rounded-md text-sm font-medium text-gray-200 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-500 rounded-md text-sm font-medium text-gray-200 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
